import type { Workflow } from '@/types/logicApps';

export const WORKFLOW_GRAPH_CARD_WIDTH = 240;
export const WORKFLOW_GRAPH_CARD_MIN_HEIGHT = 64;

export interface WorkflowDefinitionSource {
  workflow: Workflow;
  definition?: Record<string, unknown> | undefined;
}

export interface WorkflowDependencyNode {
  id: string;
  workflow: Workflow;
  name: string;
  normalizedResourceId: string;
  definition?: Record<string, unknown> | undefined;
}

export interface WorkflowDependencyEdge {
  id: string;
  source: string;
  target: string;
}

export interface WorkflowDependencyGraph {
  nodes: WorkflowDependencyNode[];
  edges: WorkflowDependencyEdge[];
}

export interface WorkflowGraphPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WorkflowDependencyLayout {
  width: number;
  height: number;
  positions: ReadonlyMap<string, WorkflowGraphPosition>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const compareText = (left: string, right: string): number => {
  const a = left.toLowerCase();
  const b = right.toLowerCase();
  if (a < b) return -1;
  if (a > b) return 1;
  return left < right ? -1 : left > right ? 1 : 0;
};

const compareCaseInsensitive = (left: string, right: string): number => {
  const a = left.toLowerCase();
  const b = right.toLowerCase();
  return a < b ? -1 : a > b ? 1 : 0;
};

export const normalizeWorkflowId = (value: string): string =>
  value.trim().replace(/\/+$/, '').toLowerCase();

const bareWorkflowName = (value: string): string => {
  const normalized = normalizeWorkflowId(value);
  const segments = normalized.split('/');
  return segments.at(-1) ?? normalized;
};

const workflowDisplayName = (workflow: Workflow): string => {
  const name = workflow.name.trim();
  const segments = name.split('/');
  return segments.at(-1) ?? name;
};

const asDefinition = (value: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(value)) return undefined;
  const nested = value.definition;
  return isRecord(nested) ? nested : value;
};

/**
 * Workflow list payloads vary by backend. Prefer only explicitly published
 * shapes and never inspect draft artifacts here.
 */
export const getEmbeddedPublishedDefinition = (
  workflow: Workflow,
): Record<string, unknown> | undefined => {
  const properties = workflow.properties as Record<string, unknown>;
  const files = properties.files;
  if (isRecord(files)) {
    const published = asDefinition(files['workflow.json']);
    if (published) return published;
  }

  const flow = properties.flow;
  if (isRecord(flow)) {
    const published = asDefinition(flow.definition);
    if (published) return published;
  }

  return asDefinition(properties.definition);
};

const isLiteralWorkflowTarget = (value: unknown): value is string => {
  if (typeof value !== 'string' || value.trim().length === 0) return false;
  const target = value.trim();
  return !target.startsWith('@') && !target.includes('@{') && !target.includes('${');
};

const getWorkflowTarget = (action: Record<string, unknown>): string | undefined => {
  if (typeof action.type !== 'string' || action.type.toLowerCase() !== 'workflow') {
    return undefined;
  }

  const inputs = action.inputs;
  if (!isRecord(inputs)) return undefined;
  const host = inputs.host;
  if (!isRecord(host)) return undefined;
  const workflow = host.workflow;
  if (!isRecord(workflow) || !isLiteralWorkflowTarget(workflow.id)) return undefined;
  return workflow.id.trim();
};

const forEachChildActionMap = (
  action: Record<string, unknown>,
  visit: (actions: unknown) => void,
): void => {
  visit(action.actions);

  const elseBranch = action.else;
  if (isRecord(elseBranch)) visit(elseBranch.actions);

  const defaultBranch = action.default;
  if (isRecord(defaultBranch)) visit(defaultBranch.actions);

  const cases = action.cases;
  if (isRecord(cases)) {
    for (const branch of Object.values(cases)) {
      if (isRecord(branch)) visit(branch.actions);
    }
  }

  const tools = action.tools;
  if (Array.isArray(tools)) {
    for (const tool of tools) {
      if (isRecord(tool)) visit(tool.actions);
    }
  } else if (isRecord(tools)) {
    for (const tool of Object.values(tools)) {
      if (isRecord(tool)) visit(tool.actions);
    }
  }

  const inputs = action.inputs;
  if (!isRecord(inputs)) return;
  const inputTools = inputs.tools;
  if (Array.isArray(inputTools)) {
    for (const tool of inputTools) {
      if (isRecord(tool)) visit(tool.actions);
    }
  } else if (isRecord(inputTools)) {
    for (const tool of Object.values(inputTools)) {
      if (isRecord(tool)) visit(tool.actions);
    }
  }
};

export const buildWorkflowDependencyGraph = (
  sources: readonly WorkflowDefinitionSource[],
): WorkflowDependencyGraph => {
  const nodes = sources
    .map(({ workflow, definition }): WorkflowDependencyNode => {
      const normalizedResourceId = normalizeWorkflowId(workflow.id);
      return {
        id: `workflow:${normalizedResourceId}`,
        workflow,
        name: workflowDisplayName(workflow),
        normalizedResourceId,
        definition: definition ?? getEmbeddedPublishedDefinition(workflow),
      };
    })
    .sort((left, right) =>
      compareCaseInsensitive(left.name, right.name) ||
      compareText(left.normalizedResourceId, right.normalizedResourceId),
    );

  const byResourceId = new Map(nodes.map((node) => [node.normalizedResourceId, node]));
  const byName = new Map<string, WorkflowDependencyNode>();
  for (const node of nodes) {
    const normalizedName = normalizeWorkflowId(node.name);
    if (!byName.has(normalizedName)) byName.set(normalizedName, node);
  }

  const edges: WorkflowDependencyEdge[] = [];
  const edgeIds = new Set<string>();

  for (const source of nodes) {
    const actions = source.definition?.actions;
    if (!actions) continue;

    const visited = new WeakSet<object>();
    const visitActionMap = (candidate: unknown): void => {
      if (!isRecord(candidate) || visited.has(candidate)) return;
      visited.add(candidate);

      for (const actionValue of Object.values(candidate)) {
        if (!isRecord(actionValue) || visited.has(actionValue)) continue;
        visited.add(actionValue);

        const targetValue = getWorkflowTarget(actionValue);
        if (targetValue) {
          const normalizedTarget = normalizeWorkflowId(targetValue);
          const target = byResourceId.get(normalizedTarget) ??
            (!normalizedTarget.includes('/') ? byName.get(bareWorkflowName(normalizedTarget)) : undefined);
          if (target) {
            const id = `workflow-edge:${source.id}->${target.id}`;
            if (!edgeIds.has(id)) {
              edgeIds.add(id);
              edges.push({ id, source: source.id, target: target.id });
            }
          }
        }

        forEachChildActionMap(actionValue, visitActionMap);
      }
    };

    visitActionMap(actions);
  }

  edges.sort((left, right) => compareText(left.id, right.id));
  return { nodes, edges };
};

const connectedComponents = (
  nodes: readonly WorkflowDependencyNode[],
  edges: readonly WorkflowDependencyEdge[],
): string[][] => {
  const neighbors = new Map(nodes.map((node) => [node.id, new Set<string>()]));
  for (const edge of edges) {
    neighbors.get(edge.source)?.add(edge.target);
    neighbors.get(edge.target)?.add(edge.source);
  }

  const visited = new Set<string>();
  const components: string[][] = [];
  for (const node of nodes) {
    if (visited.has(node.id)) continue;
    const component: string[] = [];
    const queue = [node.id];
    visited.add(node.id);
    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) continue;
      component.push(current);
      const nextIds = [...(neighbors.get(current) ?? [])].sort(compareText);
      for (const next of nextIds) {
        if (visited.has(next)) continue;
        visited.add(next);
        queue.push(next);
      }
    }
    component.sort(compareText);
    components.push(component);
  }
  return components;
};

export const layoutWorkflowDependencyGraph = (
  graph: WorkflowDependencyGraph,
  measuredHeights: ReadonlyMap<string, number>,
): WorkflowDependencyLayout => {
  const horizontalPadding = 28;
  const topPadding = 52;
  const bottomPadding = 28;
  const horizontalGap = 96;
  const verticalGap = 28;
  const componentGap = 56;
  const positions = new Map<string, WorkflowGraphPosition>();
  const nodeOrder = new Map(graph.nodes.map((node, index) => [node.id, index]));
  const components = connectedComponents(graph.nodes, graph.edges);
  let componentTop = topPadding;
  let maxRight = horizontalPadding;

  for (const component of components) {
    const componentSet = new Set(component);
    const indegree = new Map(component.map((id) => [id, 0]));
    const outgoing = new Map(component.map((id) => [id, [] as string[]]));
    for (const edge of graph.edges) {
      if (!componentSet.has(edge.source) || !componentSet.has(edge.target)) continue;
      outgoing.get(edge.source)?.push(edge.target);
      indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
    }
    for (const targets of outgoing.values()) targets.sort(compareText);

    const queue = component
      .filter((id) => (indegree.get(id) ?? 0) === 0)
      .sort((a, b) => (nodeOrder.get(a) ?? 0) - (nodeOrder.get(b) ?? 0));
    const ranks = new Map<string, number>();
    for (const id of queue) ranks.set(id, 0);

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) continue;
      const currentRank = ranks.get(current) ?? 0;
      for (const target of outgoing.get(current) ?? []) {
        ranks.set(target, Math.max(ranks.get(target) ?? 0, currentRank + 1));
        const nextIndegree = (indegree.get(target) ?? 0) - 1;
        indegree.set(target, nextIndegree);
        if (nextIndegree === 0) {
          queue.push(target);
          queue.sort((a, b) => (nodeOrder.get(a) ?? 0) - (nodeOrder.get(b) ?? 0));
        }
      }
    }

    for (const id of component) {
      if (!ranks.has(id)) ranks.set(id, 0);
    }

    const columns = new Map<number, string[]>();
    for (const id of component) {
      const rank = ranks.get(id) ?? 0;
      const column = columns.get(rank) ?? [];
      column.push(id);
      columns.set(rank, column);
    }

    let componentHeight = 0;
    for (const [rank, ids] of [...columns.entries()].sort(([a], [b]) => a - b)) {
      ids.sort((a, b) => (nodeOrder.get(a) ?? 0) - (nodeOrder.get(b) ?? 0));
      let y = componentTop;
      for (const id of ids) {
        const height = Math.max(
          WORKFLOW_GRAPH_CARD_MIN_HEIGHT,
          Math.ceil(measuredHeights.get(id) ?? WORKFLOW_GRAPH_CARD_MIN_HEIGHT),
        );
        const x =
          horizontalPadding + rank * (WORKFLOW_GRAPH_CARD_WIDTH + horizontalGap);
        positions.set(id, {
          x,
          y,
          width: WORKFLOW_GRAPH_CARD_WIDTH,
          height,
        });
        y += height + verticalGap;
        maxRight = Math.max(maxRight, x + WORKFLOW_GRAPH_CARD_WIDTH);
      }
      componentHeight = Math.max(componentHeight, y - componentTop - verticalGap);
    }

    componentTop += componentHeight + componentGap;
  }

  return {
    width: maxRight + horizontalPadding,
    height:
      components.length > 0
        ? componentTop - componentGap + bottomPadding
        : topPadding + bottomPadding,
    positions,
  };
};

export const getFirstTrigger = (
  definition: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
  const triggers = definition?.triggers;
  if (!isRecord(triggers)) return undefined;
  const firstName = Object.keys(triggers).sort(compareText)[0];
  const trigger = firstName ? triggers[firstName] : undefined;
  return isRecord(trigger) ? trigger : undefined;
};
